import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface PromoCodeValidation {
  valid: boolean;
  code?: string;
  description?: string;
  discount_type?: 'percentage' | 'fixed_amount';
  discount_value?: number;
  currency?: string;
  canvasser_name?: string;
  remaining_uses?: number | null;
  error?: string;
}

interface PromoCodeApplication {
  success: boolean;
  discount_applied?: number;
  original_amount?: number;
  final_amount?: number;
  discount_type?: 'percentage' | 'fixed_amount';
  discount_value?: number;
  canvasser_name?: string;
  error?: string;
}

export const usePromoCode = () => {
  const [isValidating, setIsValidating] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [validationResult, setValidationResult] = useState<PromoCodeValidation | null>(null);

  const validatePromoCode = async (code: string, email?: string): Promise<PromoCodeValidation> => {
    if (!code.trim()) {
      const result = { valid: false, error: 'Please enter a promo code' };
      setValidationResult(result);
      return result;
    }

    // Validate promo code format
    const sanitizedCode = code.toUpperCase().trim();
    if (!/^[A-Z0-9]{3,20}$/.test(sanitizedCode)) {
      const result = { valid: false, error: 'Invalid promo code format' };
      setValidationResult(result);
      return result;
    }

    setIsValidating(true);
    
    try {
      // Get the current session to include auth token
      const { data: { session } } = await supabase.auth.getSession();
      
      const { data, error } = await supabase.functions.invoke('validate-promo-code', {
        body: { code: sanitizedCode, email },
        headers: session?.access_token ? {
          Authorization: `Bearer ${session.access_token}`
        } : {}
      });

      if (error) {
        console.error('Promo code validation error:', error);
        const result = { valid: false, error: 'Failed to validate promo code' };
        setValidationResult(result);
        return result;
      }

      setValidationResult(data);
      
      if (data.valid) {
        toast.success(`Promo code "${sanitizedCode}" is valid! ${data.description || ''}`);
      } else {
        toast.error(data.error || 'Invalid promo code');
      }

      return data;
    } catch (err) {
      console.error('Promo validation exception:', err);
      const result = { valid: false, error: 'An error occurred while validating the promo code' };
      setValidationResult(result);
      return result;
    } finally {
      setIsValidating(false);
    }
  };

  const applyPromoCode = async (
    code: string, 
    email: string, 
    customerName: string, 
    originalAmount: number,
    subscriptionId?: string
  ): Promise<PromoCodeApplication> => {
    setIsApplying(true);
    
    try {
      // Get the current session to include auth token
      const { data: { session } } = await supabase.auth.getSession();
      
      const { data, error } = await supabase.functions.invoke('apply-promo-code', {
        body: { 
          code: code.toUpperCase().trim(), 
          email: email.toLowerCase().trim(), 
          customer_name: customerName.trim(),
          original_amount: originalAmount,
          subscription_id: subscriptionId
        },
        headers: session?.access_token ? {
          Authorization: `Bearer ${session.access_token}`
        } : {}
      });

      if (error) {
        console.error('Promo code application error:', error);
        const result = { success: false, error: 'Failed to apply promo code' };
        toast.error(result.error);
        return result;
      }

      if (data.success) {
        const savings = data.discount_applied;
        const savingsText = data.discount_type === 'percentage' 
          ? `${data.discount_value}% off (£${savings.toFixed(2)} saved)`
          : `£${savings.toFixed(2)} off`;
        
        toast.success(`Promo code applied! ${savingsText}`);
        
        if (data.canvasser_name && data.canvasser_name !== 'Special Promotion') {
          console.log(`🎯 Signup credited to canvasser: ${data.canvasser_name}`);
        }
      } else {
        toast.error(data.error || 'Failed to apply promo code');
      }

      return data;
    } catch (err) {
      console.error('Promo application exception:', err);
      const result = { success: false, error: 'An error occurred while applying the promo code' };
      toast.error(result.error);
      return result;
    } finally {
      setIsApplying(false);
    }
  };

  const clearValidation = () => {
    setValidationResult(null);
  };

  const calculateDiscountedAmount = (originalAmount: number, promoCode: PromoCodeValidation): number => {
    if (!promoCode.valid || !promoCode.discount_value) return originalAmount;
    
    let discount = 0;
    if (promoCode.discount_type === 'percentage') {
      discount = (originalAmount * promoCode.discount_value) / 100;
    } else {
      discount = promoCode.discount_value;
    }
    
    // Ensure discount doesn't exceed original amount
    discount = Math.min(discount, originalAmount);
    return originalAmount - discount;
  };

  return {
    validatePromoCode,
    applyPromoCode,
    clearValidation,
    calculateDiscountedAmount,
    isValidating,
    isApplying,
    validationResult
  };
};